import React from 'react';
import { Loader2, CheckCircle, AlertCircle, Search, Clock, RefreshCw } from 'lucide-react';

interface CheckStatusProgressProps {
  progress: number;
  status: string;
  attempt?: number;
  isComplete?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  badId?: string;
}

const CheckStatusProgress: React.FC<CheckStatusProgressProps> = ({
  progress,
  status,
  attempt,
  isComplete = false,
  hasError = false,
  errorMessage,
  badId
}) => {
  // Ensure progress is a valid number between 0 and 100
  const validProgress = Math.max(0, Math.min(100, Number(progress) || 0));
  const getProgressIcon = () => {
    if (hasError) {
      return <AlertCircle className="w-8 h-8 text-red-500" />;
    }
    
    if (isComplete) {
      return <CheckCircle className="w-8 h-8 text-green-500" />;
    }

    // Progress-based icons for status checking
    if (validProgress < 30) {
      return <Search className="w-8 h-8 text-blue-500 animate-pulse" />;
    } else if (validProgress < 60) {
      return <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />;
    } else if (validProgress < 90) {
      return <Clock className="w-8 h-8 text-blue-500 animate-pulse" />;
    } else {
      return <CheckCircle className="w-8 h-8 text-green-500 animate-pulse" />;
    }
  };

  const getProgressColor = () => {
    if (hasError) return 'bg-red-500';
    if (isComplete) return 'bg-green-500';
    if (validProgress < 30) return 'bg-blue-400';
    if (validProgress < 60) return 'bg-blue-500';
    if (validProgress < 90) return 'bg-blue-600';
    return 'bg-green-400';
  };

  const getProgressStage = () => {
    if (hasError) return 'Status Check Failed';
    if (isComplete) return 'Status Retrieved';
    if (validProgress < 30) return 'Connecting to Server';
    if (validProgress < 60) return 'Checking Analysis Status';
    if (validProgress < 90) return 'Retrieving Results';
    return 'Finalizing';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {getProgressIcon()}
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {hasError ? 'Status Check Failed' : 
             isComplete ? 'Status Retrieved Successfully!' :
             'Checking Analysis Status'}
          </h2>

          {/* Bad ID Display */}
          {badId && (
            <p className="text-sm text-gray-600 mb-2">
              Analysis ID: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{badId}</span>
            </p>
          )}

          {/* Progress Stage */}
          <p className="text-sm text-gray-600 mb-4">
            {getProgressStage()}
          </p>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${validProgress}%` }}
            ></div>
          </div>

          {/* Progress Percentage */}
          <div className="flex justify-between text-sm text-gray-600 mb-4">
            <span>{Math.round(validProgress)}%</span>
            <span>{isComplete ? '100%' : `${Math.round(validProgress)}/100`}</span>
          </div>

          {/* Status Message */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-700 flex items-center justify-center">
              {!hasError && !isComplete && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {hasError ? errorMessage : (status || 'Checking analysis status...')}
            </p>
          </div>

          {/* Attempt Info */}
          {/* {attempt && !isComplete && !hasError && (
            <p className="text-xs text-gray-500">
              Checking status... (Attempt {attempt})
            </p>
          )} */}

          {/* Status Check Steps */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                validProgress >= 25 ? 'bg-green-500' : validProgress >= 5 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={validProgress >= 25 ? 'text-green-600' : 'text-gray-500'}>
                Server Connection
              </span>
            </div>
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                validProgress >= 50 ? 'bg-green-500' : validProgress >= 30 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={validProgress >= 50 ? 'text-green-600' : 'text-gray-500'}>
                Status Verification
              </span>
            </div>
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                validProgress >= 75 ? 'bg-green-500' : validProgress >= 60 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={validProgress >= 75 ? 'text-green-600' : 'text-gray-500'}>
                Data Retrieval
              </span>
            </div>
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                validProgress >= 100 ? 'bg-green-500' : validProgress >= 90 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={validProgress >= 100 ? 'text-green-600' : 'text-gray-500'}>
                Results Ready
              </span>
            </div>
          </div>

          {/* Estimated Time */}
          {!isComplete && !hasError && (
            <div className="mt-4 text-xs text-gray-500">
              <p>This usually takes 10-30 seconds</p>
              <p>Checking your analysis status...</p>
            </div>
          )}
          
          {/* Success Message */}
          {isComplete && !hasError && (
            <div className="mt-4 text-xs text-green-600 bg-green-50 p-3 rounded-lg">
              <p className="font-medium">Status check completed!</p>
              <p>Your analysis results are ready to view.</p>
            </div>
          )}

          {/* Error Actions */}
          {hasError && (
            <div className="mt-6 space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                Try Again
              </button>
              
              {/* Show additional help for specific errors */}
              {errorMessage?.includes('not found') && (
                <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium mb-1">Analysis not found:</p>
                  <ul className="space-y-1">
                    <li>• The analysis may have expired</li>
                    <li>• Try starting a new analysis</li>
                    <li>• Check if the analysis ID is correct</li>
                  </ul>
                </div>
              )}
              
              {errorMessage?.includes('temporarily unavailable') && (
                <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium mb-1">Service temporarily unavailable:</p>
                  <ul className="space-y-1">
                    <li>• Wait a few minutes and try again</li>
                    <li>• The service may be under maintenance</li>
                    <li>• Check your internet connection</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckStatusProgress;
